import React, { useState, useRef, useEffect, useMemo } from "react";
import { ShapeStream, Shape } from "@electric-sql/client";
import { Button, TextField, Flex, Card, Text, Box } from "@radix-ui/themes";
import styled from "@emotion/styled";
import { useAtlasAuthContext } from "@/contexts/atlas-auth";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/modules/api";
import { getBackendBaseUrl } from "@/managers/backend-api-client/initializeBackendApiManager";
import Lowlight from "react-lowlight";
import { Virtuoso } from "react-virtuoso";

export const ElectricSessionEventsPage: React.FC = () => {
  const { authContext } = useAtlasAuthContext();
  const [spaceAccountId, setSpaceAccountId] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const shapeRef = useRef<Shape<Record<string, unknown>> | null>(null);
  const streamRef = useRef<ShapeStream<Record<string, unknown>> | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Fetch current user's spaces and space accounts
  const { data: spacesData, isLoading: spacesLoading } = useQuery({
    queryKey: ["spaces", "me"],
    queryFn: () => api.get("/v1/me/spaces", {}),
  });

  const { data: spaceAccountsData, isLoading: spaceAccountsLoading } = useQuery({
    queryKey: ["space-accounts", "me"],
    queryFn: () => api.get("/v1/me/space-accounts", {}),
  });

  const isLoadingSpaceData = spacesLoading || spaceAccountsLoading;

  // Find the primary space account ID
  const primarySpaceAccountId = useMemo(() => {
    if (!spacesData?.data || !spaceAccountsData?.data) return null;

    const primarySpace = spacesData.data.find(space => space.is_primary_space);
    if (!primarySpace) return null;

    const primarySpaceAccount = spaceAccountsData.data.find(
      account => account.space_id === primarySpace.id
    );

    return primarySpaceAccount?.id ?? null;
  }, [spacesData, spaceAccountsData]);

  // Prefill the space account ID when primary space account is available
  useEffect(() => {
    if (primarySpaceAccountId && !spaceAccountId) {
      setSpaceAccountId(primarySpaceAccountId);
    }
  }, [primarySpaceAccountId, spaceAccountId]);

  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  const handleSubscribe = async () => {
    if (!spaceAccountId.trim()) {
      setError("Please enter a space account ID");
      return;
    }

    try {
      setError(null);
      setData([]);

      // Create the stream with the backend proxy endpoint
      streamRef.current = new ShapeStream({
        url: `${getBackendBaseUrl()}/internal/v1/electric/v1/shape`,
        params: {
          table: "electric_session_events",
          where: `'${spaceAccountId}' = ANY(accessible_by_space_account_ids)`,
        },
        headers: {
          Authorization: `OAuthAccessToken ${authContext.authToken}`,
        },
        backoffOptions: {
          initialDelay: 1000,
          maxDelay: 60000,
          multiplier: 3,
        },
      });

      // Create the shape and subscribe
      shapeRef.current = new Shape(streamRef.current);
      unsubscribeRef.current = shapeRef.current.subscribe(({ rows }) => {
        setData(rows);
      });

      setIsSubscribed(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to subscribe");
      setIsSubscribed(false);
    }
  };

  const handleStop = () => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    if (shapeRef.current) {
      shapeRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current = null;
    }
    setIsSubscribed(false);
    setData([]);
    setError(null);
  };

  // Prepare JSON strings for each data item
  const jsonStrings = useMemo(() => {
    return data.map(item => JSON.stringify(item, null, 2));
  }, [data]);

  return (
    <Container>
      <Flex justify="between" align="center" mb="4">
        <div>
          <h1>Electric Session Events</h1>
          <DescriptionText>
            Subscribe to electric_session_events table by space account ID.
          </DescriptionText>
        </div>
      </Flex>
      <Card>
        <Flex direction="column" gap="4" p="4">
          {isLoadingSpaceData ? (
            <LoadingSection>
              <Text size="2" color="gray">
                Loading space account data...
              </Text>
            </LoadingSection>
          ) : (
            <>
              <Flex gap="3" align="center">
                <StyledTextField
                  value={spaceAccountId}
                  onChange={e => setSpaceAccountId(e.target.value)}
                  placeholder="Enter space account ID"
                  disabled={isSubscribed}
                  style={{ flex: 1 }}
                />
                {!isSubscribed ? (
                  <Button onClick={handleSubscribe} variant="solid">
                    Subscribe
                  </Button>
                ) : (
                  <Button onClick={handleStop} variant="outline" color="red">
                    Stop
                  </Button>
                )}
              </Flex>

              {error && (
                <ErrorCard>
                  <Text size="2" color="red">
                    Error: {error}
                  </Text>
                </ErrorCard>
              )}

              {isSubscribed && (
                <Box>
                  <Text size="2" weight="bold">
                    Subscribed to electric_session_events for space account: {spaceAccountId}
                  </Text>
                  <br />
                  <Text size="2" color="gray">
                    {data.length} event(s) found
                  </Text>
                </Box>
              )}

              {data.length > 0 && (
                <DataContainer>
                  <Virtuoso
                    style={{ height: "600px" }}
                    totalCount={jsonStrings.length}
                    itemContent={index => (
                      <JsonItem>
                        <ItemHeader>
                          <Text size="1" color="gray">
                            Event {index + 1}
                          </Text>
                        </ItemHeader>
                        <CodeContainer>
                          <Lowlight language="json" value={jsonStrings[index]} markers={[]} />
                        </CodeContainer>
                      </JsonItem>
                    )}
                  />
                </DataContainer>
              )}
            </>
          )}
        </Flex>
      </Card>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 20px;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
`;

const LoadingSection = styled.div`
  padding: 20px;
  text-align: center;
`;

const StyledTextField = styled(TextField.Root)`
  &[data-disabled] {
    opacity: 0.6;
    background-color: var(--gray-a3);
  }

  input:disabled {
    cursor: not-allowed;
    opacity: 1; /* Prevent double opacity */
  }
`;

const ErrorCard = styled(Card)`
  background-color: var(--red-a3);
  border: 1px solid var(--red-a6);
`;

const DataContainer = styled.div`
  background-color: var(--gray-a2);
  border-radius: var(--radius-2);
  border: 1px solid var(--gray-a5);
`;

const JsonItem = styled.div`
  padding: 12px;
  border-bottom: 1px solid var(--gray-a4);

  &:last-child {
    border-bottom: none;
  }
`;

const ItemHeader = styled.div`
  margin-bottom: 8px;
`;

const CodeContainer = styled.div`
  pre {
    margin: 0;
    padding: 12px;
    background-color: var(--gray-a2);
    border-radius: var(--radius-2);
    overflow-x: auto;
    font-size: 12px;
    line-height: 1.5;
  }

  .hljs {
    background: transparent;
  }
`;
